import { useAuth } from '@app/icare/client/auth'
import { useNavigate, useSearch } from '@tanstack/react-router'
import type { TourProps } from 'antd'

import { routeForTour } from './routeForTour'
import { nextEligible } from './sequence'
import type { TourId, TourStep } from './types'

interface RouteTourBinding {
  open: boolean
  current: number
  steps: TourProps['steps']
  onChange: (next: number) => Promise<void>
  onClose: () => void
  onFinish: () => void
}

export function useRouteTour(id: TourId, steps: TourStep[]): RouteTourBinding {
  const search = useSearch({ strict: false }) as { tour?: TourId; tourStep?: number }
  const navigate = useNavigate()
  const { ability } = useAuth()

  const open = search.tour === id
  const step = search.tourStep ?? 0

  const onChange = async (next: number) => {
    try {
      await steps[next]?.onEnter?.()
    } catch (err) {
      // biome-ignore lint/suspicious/noConsole: intentional debug log for tour step failures
      console.error(`[tour:${id}] onEnter for step ${next} failed; advancing anyway`, err)
    }
    await navigate({
      to: '.',
      search: prev => ({ ...prev, tour: id, tourStep: next }),
      replace: true,
    })
  }

  const onClose = () => {
    void navigate({
      to: '.',
      search: ({ tour: _tour, tourStep: _tourStep, ...rest }) => rest,
      replace: true,
    })
  }

  const onFinish = () => {
    const next = nextEligible(id, ability)
    if (next) void navigate(routeForTour(next))
    else onClose()
  }

  return {
    open,
    current: step,
    steps: steps.map(({ onEnter: _onEnter, ...s }) => s),
    onChange,
    onClose,
    onFinish,
  }
}
